/**
 * Tramos de circulación (paso 1.6, avance 3; ADR 0012 y 0021): cada carril se divide en tramos de
 * unos 6 m (partes iguales entre dos nodos) y cada nodo de la red es un punto de conflicto común a
 * todos los carriles. Un tramo o un nodo admite un solo auto a la vez.
 *
 * Para cada ruta entre estaciones se calcula qué celdas (tramos y nodos) ocupa el auto y en qué parte
 * de su recorrido, como fracción del largo de la ruta: el motor las convierte en ventanas de tiempo
 * con la duración del traslado y la maniobra, y solo inicia un traslado si puede reservarlas todas.
 */

import type { CirculacionCompilada } from '../dominio/ejecucion';
import type { Punto } from '../dominio/espacio';
import { LARGO_TRAMO_M, type ModeloEspacial } from './compilador';
import { distancia, proyectarEnSegmento } from './geometria';

/** Largo del auto con holgura (el ícono del plano mide 4.3 m). */
export const LARGO_AUTO_M = 4.5;
/**
 * Distancia extra a un nodo dentro de la cual el auto lo ocupa (además de medio auto): 3 m o medio
 * ancho de la vía más ancha que pasa por el nodo, si es mayor.
 */
export const MARGEN_NODO_M = 3;

/** Celdas que ocupa cada ruta del modelo espacial (índices de las estaciones, como `distancias`). */
export function celdasDeCirculacion(modelo: ModeloEspacial): CirculacionCompilada {
  const ids = new Map<string, number>();
  const celda = (clave: string) => {
    let i = ids.get(clave);
    if (i === undefined) {
      i = ids.size;
      ids.set(clave, i);
    }
    return i;
  };
  const { estaciones, grafo } = modelo;
  const n = estaciones.length;
  // Ancho de la vía más ancha que pasa por un punto (la calzada llega a medio ancho del eje).
  const anchos = new Map<number, number>();
  const anchoEn = (p: Punto): number => {
    let ancho = 0;
    for (const t of grafo.tramos) {
      if (proyectarEnSegmento(p, t.a, t.b).d < 0.05) ancho = Math.max(ancho, t.via.ancho);
    }
    return ancho;
  };
  // Nodos a menos de un largo de auto: quien gira en uno todavía ocupa el otro (bocas vecinas).
  const vecinos = grafo.nodos.map((a, i) =>
    grafo.nodos.flatMap((b, j) => (i !== j && distancia(a, b) < LARGO_AUTO_M ? [j] : [])),
  );
  const anchoDelNodo = (k: number): number => {
    let a = anchos.get(k);
    if (a === undefined) {
      a = anchoEn(grafo.nodos[k]!);
      anchos.set(k, a);
    }
    return a;
  };
  const rutas: (number[] | null)[][] = Array.from({ length: n }, () =>
    new Array<number[] | null>(n).fill(null),
  );
  const medio = LARGO_AUTO_M / 2;
  for (let i = 0; i < n; i++) {
    const a = estaciones[i]!;
    for (let j = 0; j < n; j++) {
      const b = estaciones[j]!;
      const camino = modelo.caminos[`${a.id}>${b.id}`];
      if (i === j || !camino || !a.bocaSalida || !b.bocaEntrada) continue;
      const nodo = (k: number): Punto => grafo.nodos[camino[k]!]!;
      const hA = distancia(a.centro, a.bocaSalida);
      const hB = distancia(b.bocaEntrada, b.centro);
      let largoVia = 0;
      for (let k = 0; k < camino.length - 1; k++) largoVia += distancia(nodo(k), nodo(k + 1));
      const largo = hA + largoVia + hB;
      if (largo <= 0) continue;
      // El auto solo ocupa la vía mientras parte de su largo está sobre la calzada, que llega a
      // medio ancho del eje: al entrar a su cajón cruza el carril contrario hasta el borde.
      const sobreVia: [number, number] = [
        hA - anchoDelNodo(camino[0]!) / 2 - medio,
        largo - hB + anchoDelNodo(camino[camino.length - 1]!) / 2 + medio,
      ];
      const lista: number[] = [];
      const agregar = (c: number, s1: number, s2: number) => {
        const desde = Math.max(s1, sobreVia[0]);
        const hasta = Math.min(s2, sobreVia[1]);
        if (hasta > desde) lista.push(c, desde / largo, hasta / largo);
      };
      let s = hA;
      for (let k = 0; k < camino.length; k++) {
        // Nodo: punto de conflicto común a todos los carriles.
        const margen = Math.max(MARGEN_NODO_M, anchoDelNodo(camino[k]!) / 2);
        agregar(celda(`n${camino[k]}`), s - medio - margen, s + medio + margen);
        for (const v of vecinos[camino[k]!]!) {
          if (!camino.includes(v)) agregar(celda(`n${v}`), s - medio - margen, s + medio + margen);
        }
        if (k === camino.length - 1) break;
        // Carril de la arista (un sentido): tramos iguales de unos 6 m.
        const e = distancia(nodo(k), nodo(k + 1));
        const partes = Math.max(1, Math.round(e / LARGO_TRAMO_M));
        for (let p = 0; p < partes; p++) {
          const t1 = s + (e * p) / partes;
          const t2 = s + (e * (p + 1)) / partes;
          agregar(celda(`${camino[k]}>${camino[k + 1]}:${p}`), t1 - medio, t2 + medio);
        }
        s += e;
      }
      rutas[i]![j] = lista;
    }
  }
  return { celdas: ids.size, rutas };
}
